import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { refreshCurrentUser } from '@/store/authStore';
import { createFarmerProfile, listHtx, toFrontendMembershipStatus, type BackendHtx } from '@/api/ekyc';
import { useCategories } from '@/hooks/useCategories';
import { PROVINCE_FALLBACK } from '@/constants/categoryFallbacks';
import { showToast } from '@/components/base/Toast';
import { ApiError } from '@/api/client';
import { addFarmerAttachment } from '@/store';
import { DocumentUploadSlot, type ApplyExtracted } from '@/components/feature/DocumentUploadSlot';
import { ClearableInput } from '@/components/base/ClearableInput';
import { fieldString } from '@/api/aiExtraction';
import { address as addressCheck, cccd12, fullName as nameCheck, validate } from '@/utils/validators';

const fieldClass = (hasError: boolean) => `w-full min-h-[52px] rounded-xl border bg-slate-50 px-4 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-agrida-500 focus:bg-white focus:ring-2 focus:ring-agrida-500/20 ${hasError ? 'border-red-400 bg-red-50' : 'border-slate-200'}`;

/** First-run onboarding for a self-registered Farmer with no Farmer profile yet —
 * mirrors the Flutter app's `create_profile_screen.dart` (HTX picker + fullName +
 * CCCD + optional address), which already works correctly. Reached either from the
 * post-registration success screen or via `PrivateLayout`'s redirect gate for any
 * Farmer-role user with no `farmerId`. */
export default function FarmerCreateProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { names: provinceNames, loading: provincesLoading, failed: provincesFailed } = useCategories('PROVINCE', PROVINCE_FALLBACK);

  const [htxList, setHtxList] = useState<BackendHtx[]>([]);
  const [htxId, setHtxId] = useState('');
  const [fullName, setFullName] = useState(user?.displayName ?? '');
  const [cccd, setCccd] = useState('');
  const [address, setAddress] = useState('');
  const [province, setProvince] = useState('');
  const [loadingHtx, setLoadingHtx] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cccdFileId, setCccdFileId] = useState<string | undefined>();
  // Same rules as the HTX's "Tạo hồ sơ nông hộ": an AI-filled value that is
  // wrong shows red at once, an empty required field once "Tạo hồ sơ" is pressed.
  const [submitted, setSubmitted] = useState(false);
  const allErrors: Record<string, string | undefined> = {
    fullName: validate(fullName, [nameCheck], true, 'họ tên'),
    cccd: validate(cccd, [cccd12], true, 'số CCCD'),
    address: validate(address, [addressCheck], true, 'địa chỉ'),
  };
  const errors = Object.fromEntries(
    Object.entries(allErrors).map(([k, v]) => [k, ({ fullName, cccd, address } as Record<string, string>)[k]?.trim() || submitted ? v : undefined]),
  ) as Record<string, string | undefined>;

  /** The same CCCD reading the HTX uses: fills name, number and address —
   * only what AI read, only into empty inputs unless asked to overwrite. */
  const applyCccd: ApplyExtracted = (f, { overwrite }) => {
    const put = (next: string | undefined, current: string, set: (v: string) => void) => {
      if (next && (overwrite || !current.trim())) set(next);
    };
    put(fieldString(f.fullName), fullName, setFullName);
    const id = fieldString(f.cccd);
    if (id && /^[0-9]{12}$/.test(id)) put(id, cccd, setCccd);
    put(fieldString(f.address), address, setAddress);
  };

  useEffect(() => {
    listHtx()
      .then(setHtxList)
      .catch(() => showToast('Không tải được danh sách hợp tác xã', 'error'))
      .finally(() => setLoadingHtx(false));
  }, []);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!user) return;

    setSubmitted(true);
    if (Object.values(allErrors).some(Boolean)) {
      showToast('Vui lòng sửa các ô viền đỏ', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const created = await createFarmerProfile({
        userId: user.id,
        htxId: htxId || undefined,
        fullName: fullName.trim(),
        cccd: cccd.trim(),
        phone: user.username,
        address: address.trim() || undefined,
        province: province || undefined,
      });
      // The CCCD photo read above is kept on the profile, as the HTX's form does.
      if (cccdFileId) await addFarmerAttachment(created._id, { fileId: cccdFileId, type: 'CCCD_FRONT' }).catch(() => undefined);
      refreshCurrentUser({ farmerId: created._id, htxId: created.htxId, membershipStatus: toFrontendMembershipStatus(created.membershipStatus) });
      showToast(
        !created.htxId
          ? 'Tạo hồ sơ thành công! Bạn có thể gia nhập một hợp tác xã bất cứ lúc nào sau này.'
          : created.membershipStatus === 'PENDING'
            ? 'Tạo hồ sơ thành công! Đang chờ HTX xác nhận gia nhập.'
            : 'Tạo hồ sơ nông hộ thành công!',
        'success',
      );
      navigate('/nong-ho/tong-quan');
    } catch (err) {
      if (err instanceof ApiError) showToast(err.message, 'error');
      else showToast('Tạo hồ sơ thất bại, vui lòng thử lại', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto min-h-[100dvh] max-w-[540px] bg-white px-5 py-6 sm:px-8">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-agrida-50">
          <i className="ri-user-add-line text-3xl text-agrida-600" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-extrabold text-slate-900">Hoàn thiện hồ sơ nông hộ</h1>
        <p className="mt-1 text-sm text-slate-500">Nhập thông tin cơ bản để bắt đầu sử dụng Agrida — bạn có thể chọn hợp tác xã ngay hoặc gia nhập sau.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="cp-htx" className="mb-1.5 block text-sm font-medium text-slate-800">Hợp tác xã</label>
          <select
            id="cp-htx"
            value={htxId}
            onChange={(event) => setHtxId(event.target.value)}
            className={fieldClass(false)}
            disabled={loadingHtx}
          >
            <option value="">{loadingHtx ? 'Đang tải...' : '— Chưa chọn, đăng ký sau —'}</option>
            {htxList.map((h) => (
              <option key={h._id} value={h._id}>{h.name}</option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-slate-400">Bỏ trống để đăng ký như một tài khoản độc lập — bạn vẫn dùng đầy đủ nhật ký canh tác, và có thể gia nhập hoặc nhận lời mời từ một hợp tác xã bất cứ lúc nào.</p>
        </div>

        <DocumentUploadSlot
          label="Ảnh CCCD mặt trước (AI tự điền họ tên, số CCCD, địa chỉ)"
          hint="Không bắt buộc — có thể tự nhập. Ảnh được lưu vào hồ sơ của bạn."
          docType="CCCD_FRONT"
          purpose="FARMER_IDENTITY_DOCUMENT"
          onUploaded={setCccdFileId}
          onExtracted={applyCccd}
        />

        <div>
          <label htmlFor="cp-name" className="mb-1.5 block text-sm font-medium text-slate-800">Họ tên <span className="text-red-500">*</span></label>
          <ClearableInput id="cp-name" value={fullName} onValueChange={setFullName} error={errors.fullName} inputClassName={fieldClass(false)} placeholder="Nhập họ và tên" aria-label="họ tên" />
        </div>

        <div>
          <label htmlFor="cp-cccd" className="mb-1.5 block text-sm font-medium text-slate-800">Số CCCD <span className="text-red-500">*</span></label>
          <ClearableInput
            id="cp-cccd"
            inputMode="numeric"
            maxLength={12}
            value={cccd}
            onValueChange={(v) => setCccd(v.replace(/[^0-9]/g, '').slice(0, 12))}
            error={errors.cccd}
            inputClassName={fieldClass(false)}
            placeholder="12 chữ số"
            aria-label="số CCCD"
          />
        </div>

        <div>
          <label htmlFor="cp-province" className="mb-1.5 block text-sm font-medium text-slate-800">Tỉnh/Thành phố</label>
          <select id="cp-province" value={province} onChange={(event) => setProvince(event.target.value)} className={fieldClass(false)}>
            <option value="">— Không chọn —</option>
            {provinceNames.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {/* An empty dropdown is indistinguishable from a broken one, so say which
              it is. The province list is server-side data (GET /categories); it is
              empty until the category seed has run, which is exactly how this
              looked in production — a select with nothing to pick. */}
          {!provincesLoading && provinceNames.length === 0 && (
            <p className="mt-1.5 text-sm text-amber-600">
              {provincesFailed
                ? 'Không tải được danh sách tỉnh/thành. Bạn có thể bỏ trống và cập nhật sau trong Hồ sơ.'
                : 'Danh sách tỉnh/thành chưa được cấu hình. Bạn có thể bỏ trống và cập nhật sau trong Hồ sơ.'}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cp-address" className="mb-1.5 block text-sm font-medium text-slate-800">Địa chỉ <span className="text-red-500">*</span></label>
          <ClearableInput id="cp-address" value={address} onValueChange={setAddress} error={errors.address} inputClassName={fieldClass(false)} placeholder="Ấp/xã, huyện, tỉnh" aria-label="địa chỉ" />
        </div>

        <button type="submit" disabled={submitting || loadingHtx} className="mt-2 min-h-[52px] w-full rounded-xl bg-agrida-600 px-5 text-base font-bold text-white transition-colors hover:bg-agrida-700 disabled:cursor-not-allowed disabled:opacity-60">
          {submitting ? 'Đang tạo hồ sơ...' : 'Tạo hồ sơ'}
        </button>
      </form>
    </main>
  );
}
